import React, { useState } from 'react';
import { useLocation } from '@remix-run/react';
import GreenPill from './GreenPill';
import { type SupportedCurrency, getPrices, formatPrice, CURRENCY_CONFIGS } from '~/utils/currency';

interface PricingSectionProps {
  currency?: SupportedCurrency;
  onScrollToTop?: () => void;
  onContactSales?: () => void;
  isCollapsed?: boolean;
  buttonCTA?: (billingCycle: 'Monthly' | 'Yearly', currency: SupportedCurrency, planName: 'Pro' | 'Launch') => void;
}

export default function PricingSection({
  currency = 'USD',
  onScrollToTop,
  onContactSales,
  isCollapsed = false,
  buttonCTA,
}: PricingSectionProps) {
  const location = useLocation();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if we're on onboarding or pricing pages
  const isOnPricingOrOnboardingPage = location.pathname === '/onboarding' || location.pathname === '/pricing';

  // Get fixed prices for the selected currency
  const prices = getPrices(currency);
  const currencySymbol = CURRENCY_CONFIGS[currency].symbol;

  const renderFeatures = (features: React.ReactNode[]) => {
    const shouldCollapse = isCollapsed && !isExpanded;
    const displayedFeatures = shouldCollapse ? features.slice(0, 5) : features;

    return displayedFeatures;
  };

  const handleGetStarted = (
    selectedBillingCycle: 'monthly' | 'annual',
    currency: SupportedCurrency,
    planName: 'Pro' | 'Launch',
  ) => {
    const billingCycleParam = selectedBillingCycle === 'monthly' ? 'Monthly' : 'Yearly';

    if (buttonCTA) {
      buttonCTA(billingCycleParam, currency, planName);
    } else {
      if (onScrollToTop) {
        onScrollToTop();
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleContactSales = () => {
    if (onContactSales) {
      onContactSales();
    } else {
      window.open('https://calendar.app.google/sAggpvWcDjTWUasj9', '_blank');
    }
  };

  return (
    <div id="pricing" className="w-full bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24 lg:pb-12 scroll-mt-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-6">
          <h1
            className="text-black text-4xl sm:text-5xl lg:text-6xl font-semibold mb-4"
            style={{ fontFamily: 'Inter Display' }}
          >
            The{' '}
            <span className="font-regular italic" style={{ fontFamily: 'Instrument Serif' }}>
              best
            </span>{' '}
            vibe coding tool ever
          </h1>
          <p className="text-black/60 text-lg sm:text-xl" style={{ fontFamily: 'Inter Display' }}>
            One subscription, infinite possibilities. Zero token anxiety.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center align-center justify-between mb-4 gap-4 md:gap-8">
          {/* Discount Badge */}
          <div className="flex justify-center w-fit">
            <GreenPill icon={<div className="i-ph-sparkle text-[#8DD290] w-4 h-4"></div>}>
              Upto 50% BLACK FRIDAY discount
            </GreenPill>
          </div>

          {/* Billing Toggle */}
          <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4 w-fit">
            <div className="flex items-center gap-4">
              <span
                className={`text-base font-medium transition-colors ${
                  billingCycle === 'monthly' ? 'text-black' : 'text-black/50'
                }`}
                style={{ fontFamily: 'Inter Display' }}
              >
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className="relative w-14 h-7 bg-black/20 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-black/30"
                aria-label="Toggle billing cycle"
              >
                <div
                  className={`absolute top-1 left-1 w-5 h-5 bg-black rounded-full transition-transform duration-300 ${
                    billingCycle === 'annual' ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
              <span
                className={`text-base font-medium transition-colors ${
                  billingCycle === 'annual' ? 'text-black' : 'text-black/50'
                }`}
                style={{ fontFamily: 'Inter Display' }}
              >
                Annual
              </span>
            </div>
            <span
              className="bg-[#8DD290] text-[#10110C] text-xs font-semibold px-2 py-1 rounded"
              style={{ fontFamily: 'Inter Display' }}
            >
              Additional 20% off on Annual Billing
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1400px] mx-auto">
          {/* Free Plan */}
          <div className="bg-white backdrop-blur-sm rounded-xl p-6 flex flex-col border border-black/10 hover:border-black/20 transition-all duration-300 relative overflow-visible shadow-lg">
            <h3 className="text-black text-2xl font-bold mb-6" style={{ fontFamily: 'Inter Display' }}>
              Free
            </h3>

            <div className="mb-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-black text-5xl font-medium" style={{ fontFamily: 'Inter Display' }}>
                  {currencySymbol}0
                </span>
                <span className="text-black/50 text-sm" style={{ fontFamily: 'Inter Display' }}>
                  {currency === 'INR' ? '/m' : '/month'}
                </span>
              </div>
            </div>

            <button
              onClick={() => handleGetStarted(billingCycle, currency, 'Launch')}
              disabled={!isOnPricingOrOnboardingPage}
              className="w-full bg-black text-white py-3 px-6 rounded-xl font-semibold transition-all duration-200 mb-6 shadow-[0_3px_0_0_rgba(153,161,175,0.5)] hover:shadow-[0_2px_0_0_rgba(153,161,175,1)] hover:translate-y-[1px] active:shadow-[0_1px_0_0_rgba(153,161,175,1)] active:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-[0_3px_0_0_rgba(153,161,175,0.5)] disabled:hover:translate-y-0 disabled:active:shadow-[0_3px_0_0_rgba(153,161,175,0.5)] disabled:active:translate-y-0"
              style={{ fontFamily: 'Inter Display' }}
            >
              {isOnPricingOrOnboardingPage ? 'Get Started for Free' : 'Free Plan'}
            </button>

            <ul className="space-y-2 flex-grow">
              {renderFeatures([
                <li key="1" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>20 Messages</span>
                </li>,
                <li key="2" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Build web & mobile apps</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      iOS and Android apps, SEO optimised pages, responsive dashboards
                    </div>
                  </div>
                </li>,
                <li key="3" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Variables</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      Build AI apps securely by adding your keys
                    </div>
                  </div>
                </li>,
                <li key="4" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Prompt Enhancer</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      Prompt optimisation for Anthropic, Google models
                    </div>
                  </div>
                </li>,
                <li key="5" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>AI Code Generation</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      OpenAI GPT 5.1, Claude Sonnet 4.5, Gemini 3 Pro
                    </div>
                  </div>
                </li>,
                <li key="6" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Upload existing projects from GitHub</span>
                </li>,
                <li key="7" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Authentication</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      One-click Google & Social Media Login integration
                    </div>
                  </div>
                </li>,
                <li key="8" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Code Download</span>
                </li>,
                <li key="9" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Templates</span>
                </li>,
                <li key="10" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Figma to Code</span>
                </li>,
                <li key="11" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Backend Database</span>
                </li>,
                <li key="12" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Single-click publish to internet</span>
                </li>,
                <li key="13" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Custom Domain</span>
                </li>,
              ])}
            </ul>
          </div>

          {/* Pro Plan */}
          <div className="bg-white backdrop-blur-sm rounded-xl p-6 flex flex-col border border-black/10 hover:border-black/20 transition-all duration-300 relative overflow-visible shadow-lg">
            <h3 className="text-black text-2xl font-bold mb-6" style={{ fontFamily: 'Inter Display' }}>
              Pro
            </h3>

            <div className="mb-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span
                  className="text-black/30 text-3xl font-normal line-through"
                  style={{ fontFamily: 'Inter Display' }}
                >
                  {formatPrice(prices.pro.original, currency)}
                </span>
                <span className="text-black text-5xl font-medium" style={{ fontFamily: 'Inter Display' }}>
                  {formatPrice(billingCycle === 'monthly' ? prices.pro.monthly : prices.pro.annual, currency)}
                </span>
                <span className="text-black/50 text-sm" style={{ fontFamily: 'Inter Display' }}>
                  {currency === 'INR' ? '/m' : '/month'}
                </span>
              </div>
            </div>

            <button
              onClick={() => handleGetStarted(billingCycle, currency, 'Pro')}
              className="w-full bg-black text-white py-3 px-6 rounded-xl font-semibold transition-all duration-200 mb-6 shadow-[0_3px_0_0_rgba(153,161,175,0.5)] hover:shadow-[0_2px_0_0_rgba(153,161,175,1)] hover:translate-y-[1px] active:shadow-[0_1px_0_0_rgba(153,161,175,1)] active:translate-y-[2px]"
              style={{ fontFamily: 'Inter Display' }}
            >
              Start Building Now
            </button>

            <ul className="space-y-2 flex-grow">
              {renderFeatures([
                <li
                  key="1"
                  className="flex items-start gap-2 text-black text-xs bg-[#8DD290]/10 -mx-2 px-2 py-2 rounded-lg"
                >
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div className="font-medium">200 Messages per month</div>
                  </div>
                </li>,
                <li key="2" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Build web & mobile apps</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      iOS and Android apps, SEO optimised pages, responsive dashboards
                    </div>
                  </div>
                </li>,
                <li
                  key="3"
                  className="flex items-start gap-2 text-black text-xs bg-[#8DD290]/10 -mx-2 px-2 py-2 rounded-lg"
                >
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Priority Email & Community Support</span>
                    </div>
                    <div className="text-black/50 text-[11px] mt-1 leading-relaxed">
                      We respond within 2 hours on Email and Discord
                    </div>
                  </div>
                </li>,
                <li key="4" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Variables</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      Build AI apps securely by adding your keys
                    </div>
                  </div>
                </li>,
                <li key="5" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Prompt Enhancer</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      Prompt optimisation for Anthropic, Google models
                    </div>
                  </div>
                </li>,
                <li key="6" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>AI Code Generation</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      OpenAI GPT 5.1, Claude Sonnet 4.5, Gemini 3 Pro
                    </div>
                  </div>
                </li>,
                <li
                  key="7"
                  className="flex items-start gap-2 text-black text-xs bg-[#8DD290]/10 -mx-2 px-2 py-2 rounded-lg"
                >
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }} className="flex items-center gap-2">
                    <span className="font-medium">Enhanced limits for GitHub import</span>
                  </div>
                </li>,
                <li key="8" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Authentication</div>
                    <div className="text-black/40 text-[11px] mt-1 leading-relaxed">
                      One-click Google & Social Media Login integration
                    </div>
                  </div>
                </li>,
                <li key="9" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Code Download</span>
                </li>,
                <li key="10" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Templates</span>
                </li>,
                <li key="11" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Figma to Code</span>
                </li>,
                <li key="12" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Backend Database</span>
                </li>,
                <li key="13" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Single-click publish to internet</span>
                </li>,
                <li key="14" className="flex items-start gap-2 text-black text-xs">
                  <svg className="w-3 h-3 text-black flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Custom Domain</span>
                </li>,
              ])}
            </ul>
          </div>

          {/* Unlimited Plan */}
          <div className="bg-black backdrop-blur-sm rounded-xl p-6 flex flex-col border border-white/10 hover:border-white/20 transition-all duration-300 relative overflow-visible shadow-lg">
            {/* Most Value Badge */}
            <div className="absolute top-6 right-6 z-10">
              <GreenPill icon={<div className="i-ph-heart text-[#8DD290] w-4 h-4"></div>}>Most popular</GreenPill>
            </div>

            <h3 className="text-white text-2xl font-bold mb-6" style={{ fontFamily: 'Inter Display' }}>
              Unlimited
            </h3>

            <div className="mb-6">
              <div className="flex items-baseline gap-2 mb-2">
                <span
                  className="text-white/30 text-3xl font-normal line-through"
                  style={{ fontFamily: 'Inter Display' }}
                >
                  {formatPrice(prices.unlimited.original, currency)}
                </span>
                <span className="text-white text-5xl font-medium" style={{ fontFamily: 'Inter Display' }}>
                  {formatPrice(
                    billingCycle === 'monthly' ? prices.unlimited.monthly : prices.unlimited.annual,
                    currency,
                  )}
                </span>
                <span className="text-white/50 text-sm" style={{ fontFamily: 'Inter Display' }}>
                  {currency === 'INR' ? '/m' : '/month'}
                </span>
              </div>
            </div>

            <button
              onClick={() => handleGetStarted(billingCycle, currency, 'Launch')}
              className="w-full bg-white text-black py-3 px-6 rounded-xl font-semibold transition-all duration-200 mb-6 shadow-[0_3px_0_0_rgba(153,161,175,1)] hover:shadow-[0_2px_0_0_rgba(153,161,175,1)] hover:translate-y-[1px] active:shadow-[0_1px_0_0_rgba(153,161,175,1)] active:translate-y-[2px]"
              style={{ fontFamily: 'Inter Display' }}
            >
              Start Building Now
            </button>

            <ul className="space-y-2 flex-grow">
              {renderFeatures([
                <li
                  key="1"
                  className="flex items-start gap-2 text-white text-xs bg-[#8DD290]/20 -mx-2 px-2 py-2 rounded-lg"
                >
                  <svg className="w-3 h-3 text-[#8DD290] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div className="flex items-center gap-2">
                      <span className="text-white">Unlimited Messages to build stress-free</span>
                    </div>
                  </div>
                </li>,
                <li key="2" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Build web & mobile apps</div>
                    <div className="text-white/40 text-[11px] mt-1 leading-relaxed">
                      iOS and Android apps, SEO optimised pages, responsive dashboards
                    </div>
                  </div>
                </li>,
                <li
                  key="3"
                  className="flex items-start gap-2 text-white text-xs bg-[#8DD290]/20 -mx-2 px-2 py-2 rounded-lg"
                >
                  <svg className="w-3 h-3 text-[#8DD290] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div className="flex items-center gap-2">
                      <span className="text-white">Dedicated Support</span>
                    </div>
                    <div className="text-white/50 text-[11px] mt-1 leading-relaxed">
                      Speak to our product expert 1-to-1 anytime
                    </div>
                  </div>
                </li>,
                <li key="4" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Variables</div>
                    <div className="text-white/40 text-[11px] mt-1 leading-relaxed">
                      Build AI apps securely by adding your keys
                    </div>
                  </div>
                </li>,
                <li key="5" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Prompt Enhancer</div>
                    <div className="text-white/40 text-[11px] mt-1 leading-relaxed">
                      Prompt optimisation for Anthropic, Google models
                    </div>
                  </div>
                </li>,
                <li key="6" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>AI Code Generation</div>
                    <div className="text-white/40 text-[11px] mt-1 leading-relaxed">
                      OpenAI GPT 5.1, Claude Sonnet 4.5, Gemini 3 Pro
                    </div>
                  </div>
                </li>,
                <li
                  key="7"
                  className="flex items-start gap-2 text-white text-xs bg-[#8DD290]/20 -mx-2 px-2 py-2 rounded-lg"
                >
                  <svg className="w-3 h-3 text-[#8DD290] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }} className="flex items-center gap-2">
                    <span className="text-white">Enhanced limits for GitHub import</span>
                    {/* <span className="i-ph-sparkle text-[#8DD290] w-3 h-3"></span> */}
                  </div>
                </li>,
                <li key="8" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div style={{ fontFamily: 'Inter Display' }}>
                    <div>Authentication</div>
                    <div className="text-white/40 text-[11px] mt-1 leading-relaxed">
                      One-click Google & Social Media Login integration
                    </div>
                  </div>
                </li>,
                <li key="9" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Code Download</span>
                </li>,
                <li key="10" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Templates</span>
                </li>,
                <li key="11" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Figma to Code</span>
                </li>,
                <li key="12" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Backend Database</span>
                </li>,
                <li key="13" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Single-click publish to internet</span>
                </li>,
                <li key="14" className="flex items-start gap-2 text-white text-xs">
                  <svg className="w-3 h-3 text-white flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span style={{ fontFamily: 'Inter Display' }}>Custom Domain</span>
                </li>,
              ])}
            </ul>
          </div>
        </div>

        {/* Common Expand Button */}
        {isCollapsed && (
          <div className="flex justify-center mt-8">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-black text-sm font-medium hover:opacity-70 transition-opacity flex items-center gap-2 px-6 py-3 border border-black/20 rounded-xl hover:border-black/40"
              style={{ fontFamily: 'Inter Display' }}
            >
              {isExpanded ? 'Show Less' : 'Show All Features'}
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Not Sure Card */}
        <div className="max-w-[1400px] mx-auto mt-8">
          <div className="bg-white border border-black/10 rounded-xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 hover:border-black/20 transition-all duration-300 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-black/5 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-black text-xl font-semibold mb-1" style={{ fontFamily: 'Inter Display' }}>
                  Not sure what works for you?
                </h3>
                <p className="text-black/60 text-sm" style={{ fontFamily: 'Inter Display' }}>
                  Speak to our vibe coding specialist and get a free consultation.
                </p>
              </div>
            </div>
            <button
              onClick={handleContactSales}
              className="bg-black text-white py-3 px-8 rounded-xl font-semibold transition-all duration-200 whitespace-nowrap shadow-[0_3px_0_0_rgba(153,161,175,0.5)] hover:shadow-[0_2px_0_0_rgba(153,161,175,1)] hover:translate-y-[1px] active:shadow-[0_1px_0_0_rgba(153,161,175,1)] active:translate-y-[2px]"
              style={{ fontFamily: 'Inter Display' }}
            >
              Talk to us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
